import Analytics from '@withlocals/withlocals-analytics';
import { isExcluded } from 'hoc/ExcludeOnPlatforms';
import Cookies from 'js-cookie';
import lscache from 'lscache';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { WlWindow } from 'typings/globals/cordova';
import { Trek } from 'utils/analytics';
import { isIosApp } from 'utils/platform';

import { CONSENT_COOKIES_KEYS, PLATFORMS } from '../constants';

export type CookieConsent = {
  base: boolean;
  analytics: boolean;
  advertisement: boolean;
  social: boolean;
  personalised_ads: boolean;
  location: boolean;
};

type CookieConsentContextType = CookieConsent & {
  setConsent: (state: CookieConsent) => void;
};
declare const window: WlWindow;

export const setConsentValue = (
  key: string,
  value: string,
  expiresIn = 9999,
) => {
  if (isIosApp()) {
    lscache.set(key, value, expiresIn);
  } else {
    Cookies.set(key, value, { expires: expiresIn });
  }
};

const setCookies = ({
  base,
  advertisement,
  social,
  personalised_ads,
  analytics,
  location,
}: CookieConsent) => {
  setConsentValue(CONSENT_COOKIES_KEYS.CONSENT_SAVED, base.toString());
  setConsentValue(CONSENT_COOKIES_KEYS.ANALYTICS, analytics.toString());
  setConsentValue(
    CONSENT_COOKIES_KEYS.PERSONALISED_ADS,
    personalised_ads.toString(),
  );
  setConsentValue(CONSENT_COOKIES_KEYS.ADVERTISEMENT, advertisement.toString());
  setConsentValue(CONSENT_COOKIES_KEYS.SOCIAL, social.toString());
  setConsentValue(CONSENT_COOKIES_KEYS.LOCATION, location.toString());
};
const notifyAnalytics = (settings: CookieConsent) => {
  // If checks for new analytics lib, can be removed when new version is live.
  if (Analytics.setAdvertisementConsent) {
    Analytics.setAdvertisementConsent(settings.advertisement);
  }
  if (Analytics.setSocialConsent) {
    Analytics.setSocialConsent(settings.social);
  }

  if (settings.advertisement && settings.social) {
    Analytics.track('Cookie Consent', 'Default + advertisement + social');
  } else if (settings.advertisement) {
    Analytics.track('Cookie Consent', 'Default + advertisement');
  } else if (settings.social) {
    Analytics.track('Cookie Consent', 'Default + social');
  } else {
    Analytics.track('Cookie Consent', 'Default');
  }
};

const isConsentGivenOrApp = (name: string) => {
  const isNeeded = !isExcluded([PLATFORMS.SERVER]);
  if (isNeeded) {
    if (isIosApp()) {
      return lscache.get(name) === 'true';
    }
    return Cookies.get(name) === 'true';
  }
  return false;
};

export const isConsentSaved = () => {
  return isConsentGivenOrApp(CONSENT_COOKIES_KEYS.CONSENT_SAVED);
};
export const getCookieConsent = () => ({
  base: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.CONSENT_SAVED),
  advertisement: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.ADVERTISEMENT),
  analytics: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.ANALYTICS),
  personalised_ads: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.PERSONALISED_ADS),
  social: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.SOCIAL),
  location: isConsentGivenOrApp(CONSENT_COOKIES_KEYS.LOCATION),
});

export const getCookieConsentSerialized = (): string => {
  const { base, advertisement, social, location } = getCookieConsent();
  let serialized = '';
  if (base) {
    serialized += 'b';
  }
  if (advertisement) {
    serialized += 'a';
  }
  if (social) {
    serialized += 's';
  }
  if (location) {
    serialized += 'l';
  }
  return serialized;
};

const CookieConsentContext = React.createContext<CookieConsentContextType>({
  base: false,
  analytics: false,
  personalised_ads: false,
  advertisement: false,
  social: false,
  location: false,
  setConsent: setCookies,
});
export const DefaultCookieOpts = {
  expires: 9999,
};

export function CookieConsentContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, setState] = useState<CookieConsent>(getCookieConsent());
  // this enabled GA to immediately set consent values correct.
  window.consent = state;

  const setConsent = useCallback((settings: CookieConsent) => {
    setCookies(settings);
    setState(settings);
    notifyAnalytics(settings);
  }, []);
  const context = useMemo(() => {
    return { ...state, setConsent };
  }, [setConsent, state]);
  const trekkerContext = useMemo(() => {
    return {
      cookieConsent: {
        functional: state.base,
        analytics: state.base,
        advertisement: state.advertisement,
        personalised_ads: state.personalised_ads,
        social: state.social,
        location: state.location,
      },
    };
  }, [state]);

  return (
    <CookieConsentContext.Provider value={context}>
      <Trek context={trekkerContext}>{children}</Trek>
    </CookieConsentContext.Provider>
  );
}

let hasCheckedConsent = false;

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}

export function useConsentSerialized() {
  const { setConsent, ...state } = useCookieConsent();
  const setConsentFromSerialized = useCallback(
    (input: string) => {
      state.base = input.includes('b');
      state.advertisement = input.includes('a');
      state.social = input.includes('s');
      state.location = input.includes('l');
      setConsent(state);
    },
    [setConsent, state],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('consent') && !hasCheckedConsent) {
      setConsentFromSerialized(params.get('consent') as string);
      hasCheckedConsent = true;
    }
  });

  return { setConsentFromSerialized };
}
